<template>
  <div class="post-composer">
    <div class="composer-toolbar">
      <button type="button" class="small toolbar-btn" title="Bold" @click="insertMarkdown('**', '**', 'bold text')">
        <strong>B</strong>
      </button>
      <button type="button" class="small toolbar-btn" title="Italic" @click="insertMarkdown('*', '*', 'italic text')">
        <em>I</em>
      </button>
      <button type="button" class="small toolbar-btn" title="Inline code" @click="insertMarkdown('`', '`', 'code')">
        <code>{ }</code>
      </button>
      <button type="button" class="small toolbar-btn" title="Unordered list" @click="insertListItem">
        &bull; List
      </button>
      <span class="toolbar-sep"></span>
      <button
        type="button"
        class="small toolbar-btn"
        :class="{ active: showPreview }"
        @click="showPreview = !showPreview"
      >
        {{ showPreview ? 'Edit' : 'Preview' }}
      </button>
    </div>

    <div v-if="showPreview" class="composer-preview">
      <MarkdownRenderer :content="body" />
      <p v-if="!body" class="preview-placeholder">
        Nothing to preview yet.
      </p>
    </div>

    <textarea
      v-else
      ref="textareaRef"
      v-model="body"
      class="composer-textarea"
      :placeholder="$t('default.discussions.composer_placeholder')"
      :maxlength="MAX_LENGTH"
      rows="5"
    ></textarea>

    <div class="composer-footer">
      <span class="char-count" :class="{ 'char-count--warn': charCountWarn }">
        {{ body.length }} / {{ MAX_LENGTH }}
      </span>

      <div class="composer-actions">
        <button
          v-if="showCancel"
          type="button"
          class="red small"
          @click="$emit('cancel')"
        >
          {{ $t('default.discussions.cancel') }}
        </button>

        <button
          type="button"
          class="green"
          :disabled="submitting || !canSubmit"
          @click="handleSubmit"
        >
          {{ submitting ? $t('default.discussions.submitting') : submitLabel }}
        </button>
      </div>
    </div>

    <div v-if="validationError" class="composer-error">
      {{ validationError }}
    </div>
    <div v-else-if="error" class="composer-error">
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import MarkdownRenderer from '@/components/shared/MarkdownRenderer'

const MAX_LENGTH = 10000

const props = defineProps({
  initialBody: {
    type: String,
    default: '',
  },
  submitting: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
  submitLabel: {
    type: String,
    default: 'Post',
  },
  showCancel: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['submit', 'cancel'])

const body = ref(props.initialBody || '')
const showPreview = ref(false)
const textareaRef = ref(null)
const validationError = ref(null)

watch(() => props.initialBody, (val) => {
  body.value = val || ''
})

const charCountWarn = computed(() => body.value.length > MAX_LENGTH * 0.9)
const canSubmit = computed(() => body.value.trim().length > 0)

function handleSubmit() {
  validationError.value = null
  if (!canSubmit.value) {
    validationError.value = 'Please enter a message.'
    return
  }
  emit('submit', body.value.trim())
}

function resetBody() {
  body.value = ''
  validationError.value = null
}

defineExpose({ resetBody })

function insertMarkdown(prefix, suffix, placeholder) {
  const textarea = textareaRef.value
  if (!textarea) {
    body.value += `${prefix}${placeholder}${suffix}`
    return
  }
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const selected = body.value.substring(start, end) || placeholder
  const before = body.value.substring(0, start)
  const after = body.value.substring(end)
  body.value = `${before}${prefix}${selected}${suffix}${after}`
  const newPos = start + prefix.length + selected.length + suffix.length
  setTimeout(() => {
    textarea.focus()
    textarea.setSelectionRange(newPos, newPos)
  }, 0)
}

function insertListItem() {
  const textarea = textareaRef.value
  if (!textarea) {
    body.value += '\n- '
    return
  }
  const start = textarea.selectionStart
  const before = body.value.substring(0, start)
  const after = body.value.substring(start)
  const needsNewline = before.length > 0 && !before.endsWith('\n')
  const insertion = `${needsNewline ? '\n' : ''}- `
  body.value = `${before}${insertion}${after}`
  const newPos = start + insertion.length
  setTimeout(() => {
    textarea.focus()
    textarea.setSelectionRange(newPos, newPos)
  }, 0)
}
</script>

<style scoped>
.post-composer {
  width: 100%;
}

.composer-toolbar {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
  padding: 0.3rem 0;
  border-bottom: 1px solid var(--gray-200);
}

.toolbar-btn {
  font-family: 'Roboto', sans-serif;
  min-width: 2.2rem;
}

.toolbar-btn.active {
  background: var(--halsa-green);
  color: white;
}

.toolbar-sep {
  width: 1px;
  height: 1.25rem;
  background: var(--gray-200);
  margin: 0 0.2rem;
}

.composer-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  padding: 0.75rem;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  line-height: 1.5;
  resize: vertical;
  min-height: 8rem;
  color: var(--text-black);
  background: white;
  transition: border-color 0.15s;
}

.composer-textarea:focus {
  outline: none;
  border-color: var(--halsa-green);
}

.composer-preview {
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  padding: 0.75rem;
  min-height: 8rem;
  background: var(--gray-100);
}

.preview-placeholder {
  color: var(--text-grey);
  font-style: italic;
}

.composer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.char-count {
  font-size: 0.8rem;
  color: var(--text-grey);
}

.char-count--warn {
  color: var(--halsa-red);
}

.composer-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.composer-error {
  margin-top: 0.4rem;
  font-size: 0.85rem;
  color: var(--halsa-red);
}

@media (max-width: 375px) {
  .composer-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .composer-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
